const CHAPTER_CODE = /^\d{2}$/;
const LOCALES = ["pt-BR", "es", "en"] as const;

type ChapterLocale = (typeof LOCALES)[number];

/**
 * Official Harmonized System chapter titles. These name a classification
 * bucket. They are not rates, treatments, or a published ruleset.
 */
const CHAPTER_TITLES = {
  "01": { "pt-BR": "Animais vivos", es: "Animales vivos", en: "Live animals" },
  "02": { "pt-BR": "Carnes e miudezas", es: "Carnes y despojos", en: "Meat and edible offal" },
  "03": { "pt-BR": "Peixes e crustáceos", es: "Pescados y crustáceos", en: "Fish and crustaceans" },
  "04": {
    "pt-BR": "Leite, ovos e mel",
    es: "Leche, huevos y miel",
    en: "Dairy, eggs and honey",
  },
  "05": {
    "pt-BR": "Produtos de origem animal",
    es: "Productos de origen animal",
    en: "Animal products",
  },
  "06": { "pt-BR": "Plantas vivas", es: "Plantas vivas", en: "Live plants" },
  "07": { "pt-BR": "Hortaliças", es: "Hortalizas", en: "Edible vegetables" },
  "08": {
    "pt-BR": "Frutas e frutos de casca rija",
    es: "Frutas y frutos secos",
    en: "Fruit and nuts",
  },
  "09": {
    "pt-BR": "Café, chá, mate e especiarias",
    es: "Café, té, mate y especias",
    en: "Coffee, tea, mate and spices",
  },
  "10": { "pt-BR": "Cereais", es: "Cereales", en: "Cereals" },
  "11": {
    "pt-BR": "Produtos da indústria de moagem",
    es: "Productos de la molinería",
    en: "Milling products",
  },
  "12": {
    "pt-BR": "Sementes e frutos oleaginosos",
    es: "Semillas y frutos oleaginosos",
    en: "Oil seeds",
  },
  "13": { "pt-BR": "Gomas e resinas", es: "Gomas y resinas", en: "Lacs, gums and resins" },
  "14": {
    "pt-BR": "Matérias para entrançar",
    es: "Materias para trenzar",
    en: "Vegetable plaiting materials",
  },
  "15": { "pt-BR": "Gorduras e óleos", es: "Grasas y aceites", en: "Fats and oils" },
  "16": {
    "pt-BR": "Preparações de carne e peixe",
    es: "Preparaciones de carne y pescado",
    en: "Meat and fish preparations",
  },
  "17": { "pt-BR": "Açúcares", es: "Azúcares", en: "Sugars" },
  "18": { "pt-BR": "Cacau", es: "Cacao", en: "Cocoa" },
  "19": {
    "pt-BR": "Preparações de cereais",
    es: "Preparaciones de cereales",
    en: "Cereal preparations",
  },
  "20": {
    "pt-BR": "Preparações de hortaliças e frutas",
    es: "Preparaciones de hortalizas y frutas",
    en: "Vegetable and fruit preparations",
  },
  "21": {
    "pt-BR": "Preparações alimentícias diversas",
    es: "Preparaciones alimenticias diversas",
    en: "Miscellaneous edible preparations",
  },
  "22": { "pt-BR": "Bebidas e vinagres", es: "Bebidas y vinagres", en: "Beverages and vinegar" },
  "23": {
    "pt-BR": "Resíduos das indústrias alimentares",
    es: "Residuos de las industrias alimentarias",
    en: "Food industry residues",
  },
  "24": { "pt-BR": "Tabaco", es: "Tabaco", en: "Tobacco" },
  "25": {
    "pt-BR": "Sal, enxofre e pedras",
    es: "Sal, azufre y piedras",
    en: "Salt, sulphur and stone",
  },
  "26": { "pt-BR": "Minérios", es: "Minerales", en: "Ores" },
  "27": { "pt-BR": "Combustíveis minerais", es: "Combustibles minerales", en: "Mineral fuels" },
  "28": {
    "pt-BR": "Produtos químicos inorgânicos",
    es: "Productos químicos inorgánicos",
    en: "Inorganic chemicals",
  },
  "29": {
    "pt-BR": "Produtos químicos orgânicos",
    es: "Productos químicos orgánicos",
    en: "Organic chemicals",
  },
  "30": { "pt-BR": "Produtos farmacêuticos", es: "Productos farmacéuticos", en: "Pharmaceuticals" },
  "31": { "pt-BR": "Adubos", es: "Abonos", en: "Fertilizers" },
  "32": {
    "pt-BR": "Extratos tanantes e corantes",
    es: "Extractos curtientes y colorantes",
    en: "Tanning and dyeing extracts",
  },
  "33": { "pt-BR": "Óleos essenciais", es: "Aceites esenciales", en: "Essential oils" },
  "34": {
    "pt-BR": "Sabões e preparações de limpeza",
    es: "Jabones y preparaciones de limpieza",
    en: "Soap and cleaning preparations",
  },
  "35": {
    "pt-BR": "Matérias albuminoides",
    es: "Materias albuminoideas",
    en: "Albuminoidal substances",
  },
  "36": { "pt-BR": "Explosivos", es: "Explosivos", en: "Explosives" },
  "37": {
    "pt-BR": "Produtos fotográficos",
    es: "Productos fotográficos",
    en: "Photographic goods",
  },
  "38": {
    "pt-BR": "Produtos químicos diversos",
    es: "Productos químicos diversos",
    en: "Miscellaneous chemical products",
  },
  "39": { "pt-BR": "Plásticos", es: "Plásticos", en: "Plastics" },
  "40": { "pt-BR": "Borracha", es: "Caucho", en: "Rubber" },
  "41": { "pt-BR": "Peles e couros", es: "Pieles y cueros", en: "Raw hides and skins" },
  "42": { "pt-BR": "Obras de couro", es: "Manufacturas de cuero", en: "Leather articles" },
  "43": { "pt-BR": "Pelesteria", es: "Peletería", en: "Furskins" },
  "44": { "pt-BR": "Madeira", es: "Madera", en: "Wood" },
  "45": { "pt-BR": "Cortiça", es: "Corcho", en: "Cork" },
  "46": {
    "pt-BR": "Obras de espartaria",
    es: "Manufacturas de espartería",
    en: "Straw manufactures",
  },
  "47": { "pt-BR": "Pastas de madeira", es: "Pastas de madera", en: "Wood pulp" },
  "48": { "pt-BR": "Papel e cartão", es: "Papel y cartón", en: "Paper and paperboard" },
  "49": {
    "pt-BR": "Livros e impressos",
    es: "Libros e impresos",
    en: "Printed books and newspapers",
  },
  "50": { "pt-BR": "Seda", es: "Seda", en: "Silk" },
  "51": { "pt-BR": "Lã", es: "Lana", en: "Wool" },
  "52": { "pt-BR": "Algodão", es: "Algodón", en: "Cotton" },
  "53": {
    "pt-BR": "Outras fibras têxteis vegetais",
    es: "Otras fibras textiles vegetales",
    en: "Other vegetable textile fibres",
  },
  "54": { "pt-BR": "Filamentos sintéticos", es: "Filamentos sintéticos", en: "Man-made filaments" },
  "55": {
    "pt-BR": "Fibras sintéticas descontínuas",
    es: "Fibras sintéticas discontinuas",
    en: "Man-made staple fibres",
  },
  "56": {
    "pt-BR": "Feltros e não tecidos",
    es: "Fieltros y telas sin tejer",
    en: "Wadding, felt and nonwovens",
  },
  "57": { "pt-BR": "Tapetes", es: "Alfombras", en: "Carpets" },
  "58": { "pt-BR": "Tecidos especiais", es: "Tejidos especiales", en: "Special woven fabrics" },
  "59": { "pt-BR": "Tecidos impregnados", es: "Tejidos impregnados", en: "Impregnated textiles" },
  "60": { "pt-BR": "Tecidos de malha", es: "Tejidos de punto", en: "Knitted fabrics" },
  "61": { "pt-BR": "Vestuário de malha", es: "Prendas de punto", en: "Knitted apparel" },
  "62": {
    "pt-BR": "Vestuário, exceto malha",
    es: "Prendas, excepto de punto",
    en: "Woven apparel",
  },
  "63": {
    "pt-BR": "Outros artefatos têxteis",
    es: "Los demás artículos textiles",
    en: "Other made-up textiles",
  },
  "64": { "pt-BR": "Calçados", es: "Calzado", en: "Footwear" },
  "65": { "pt-BR": "Chapéus", es: "Sombreros", en: "Headgear" },
  "66": { "pt-BR": "Guarda-chuvas", es: "Paraguas", en: "Umbrellas" },
  "67": {
    "pt-BR": "Penas e flores artificiais",
    es: "Plumas y flores artificiales",
    en: "Feathers and artificial flowers",
  },
  "68": { "pt-BR": "Obras de pedra", es: "Manufacturas de piedra", en: "Articles of stone" },
  "69": { "pt-BR": "Produtos cerâmicos", es: "Productos cerámicos", en: "Ceramic products" },
  "70": { "pt-BR": "Vidro", es: "Vidrio", en: "Glass" },
  "71": {
    "pt-BR": "Pérolas e metais preciosos",
    es: "Perlas y metales preciosos",
    en: "Pearls and precious metals",
  },
  "72": { "pt-BR": "Ferro e aço", es: "Hierro y acero", en: "Iron and steel" },
  "73": {
    "pt-BR": "Obras de ferro ou aço",
    es: "Manufacturas de hierro o acero",
    en: "Articles of iron or steel",
  },
  "74": { "pt-BR": "Cobre", es: "Cobre", en: "Copper" },
  "75": { "pt-BR": "Níquel", es: "Níquel", en: "Nickel" },
  "76": { "pt-BR": "Alumínio", es: "Aluminio", en: "Aluminium" },
  "77": { "pt-BR": "Capítulo reservado", es: "Capítulo reservado", en: "Reserved chapter" },
  "78": { "pt-BR": "Chumbo", es: "Plomo", en: "Lead" },
  "79": { "pt-BR": "Zinco", es: "Cinc", en: "Zinc" },
  "80": { "pt-BR": "Estanho", es: "Estaño", en: "Tin" },
  "81": {
    "pt-BR": "Outros metais comuns",
    es: "Los demás metales comunes",
    en: "Other base metals",
  },
  "82": { "pt-BR": "Ferramentas", es: "Herramientas", en: "Tools" },
  "83": {
    "pt-BR": "Obras diversas de metais comuns",
    es: "Manufacturas diversas de metales comunes",
    en: "Miscellaneous base-metal articles",
  },
  "84": {
    "pt-BR": "Máquinas e aparelhos mecânicos",
    es: "Máquinas y aparatos mecánicos",
    en: "Machinery",
  },
  "85": {
    "pt-BR": "Máquinas e aparelhos elétricos",
    es: "Máquinas y aparatos eléctricos",
    en: "Electrical machinery",
  },
  "86": { "pt-BR": "Material ferroviário", es: "Material ferroviario", en: "Railway locomotives" },
  "87": { "pt-BR": "Veículos automóveis", es: "Vehículos automóviles", en: "Vehicles" },
  "88": { "pt-BR": "Aeronaves", es: "Aeronaves", en: "Aircraft" },
  "89": { "pt-BR": "Embarcações", es: "Embarcaciones", en: "Ships" },
  "90": {
    "pt-BR": "Instrumentos de óptica e medida",
    es: "Instrumentos de óptica y medida",
    en: "Optical and measuring instruments",
  },
  "91": { "pt-BR": "Relógios", es: "Relojes", en: "Clocks and watches" },
  "92": {
    "pt-BR": "Instrumentos musicais",
    es: "Instrumentos musicales",
    en: "Musical instruments",
  },
  "93": { "pt-BR": "Armas e munições", es: "Armas y municiones", en: "Arms and ammunition" },
  "94": { "pt-BR": "Móveis", es: "Muebles", en: "Furniture" },
  "95": { "pt-BR": "Brinquedos e jogos", es: "Juguetes y juegos", en: "Toys and games" },
  "96": {
    "pt-BR": "Obras diversas",
    es: "Manufacturas diversas",
    en: "Miscellaneous manufactured articles",
  },
  "97": { "pt-BR": "Objetos de arte", es: "Objetos de arte", en: "Works of art" },
} as const;

export type NcmChapterCode = keyof typeof CHAPTER_TITLES;

export const NCM_CHAPTER_CODES = (Object.keys(CHAPTER_TITLES) as NcmChapterCode[]).sort(
  (left, right) => left.localeCompare(right),
);

export function isNcmChapterCode(value: string): value is NcmChapterCode {
  return Object.hasOwn(CHAPTER_TITLES, value);
}

function isChapterLocale(value: string): value is ChapterLocale {
  return LOCALES.some((locale) => locale === value);
}

export function ncmChapterCode(ncm: string | null | undefined): NcmChapterCode | null {
  if (!ncm) return null;
  const digits = ncm.replace(/\D/g, "");
  if (digits.length < 2) return null;
  const chapter = digits.slice(0, 2);
  return isNcmChapterCode(chapter) ? chapter : null;
}

/** Reads an NCM already labeled in excerpt text. Does not invent a code. */
export function ncmCodeFromLabeledText(value: string): string | null {
  const match = value.match(/\bNCM\s*[:#-]?\s*([0-9.]{4,12})\b/i);
  if (!match?.[1]) return null;
  const digits = match[1].replace(/\D/g, "");
  return digits.length === 8 ? digits : null;
}

export function ncmChapterTitle(chapter: string, locale: string): string | null {
  if (!CHAPTER_CODE.test(chapter) || !isNcmChapterCode(chapter) || !isChapterLocale(locale)) {
    return null;
  }
  return CHAPTER_TITLES[chapter][locale];
}

export function ncmChapterTitles(chapter: string): Record<ChapterLocale, string> | null {
  if (!isNcmChapterCode(chapter)) return null;
  return CHAPTER_TITLES[chapter];
}

/** Official HS section letters. Classification only — not a rate table. */
export type HsSectionCode =
  | "I"
  | "II"
  | "III"
  | "IV"
  | "V"
  | "VI"
  | "VII"
  | "VIII"
  | "IX"
  | "X"
  | "XI"
  | "XII"
  | "XIII"
  | "XIV"
  | "XV"
  | "XVI"
  | "XVII"
  | "XVIII"
  | "XIX"
  | "XX"
  | "XXI";

const SECTION_RANGES: ReadonlyArray<{ id: HsSectionCode; from: number; to: number }> = [
  { id: "I", from: 1, to: 5 },
  { id: "II", from: 6, to: 14 },
  { id: "III", from: 15, to: 15 },
  { id: "IV", from: 16, to: 24 },
  { id: "V", from: 25, to: 27 },
  { id: "VI", from: 28, to: 38 },
  { id: "VII", from: 39, to: 40 },
  { id: "VIII", from: 41, to: 43 },
  { id: "IX", from: 44, to: 46 },
  { id: "X", from: 47, to: 49 },
  { id: "XI", from: 50, to: 63 },
  { id: "XII", from: 64, to: 67 },
  { id: "XIII", from: 68, to: 70 },
  { id: "XIV", from: 71, to: 71 },
  { id: "XV", from: 72, to: 83 },
  { id: "XVI", from: 84, to: 85 },
  { id: "XVII", from: 86, to: 89 },
  { id: "XVIII", from: 90, to: 92 },
  { id: "XIX", from: 93, to: 93 },
  { id: "XX", from: 94, to: 96 },
  { id: "XXI", from: 97, to: 97 },
];

const SECTION_TITLES: Record<HsSectionCode, Record<ChapterLocale, string>> = {
  I: {
    "pt-BR": "Animais vivos e produtos do reino animal",
    es: "Animales vivos y productos del reino animal",
    en: "Live animals; animal products",
  },
  II: {
    "pt-BR": "Produtos do reino vegetal",
    es: "Productos del reino vegetal",
    en: "Vegetable products",
  },
  III: {
    "pt-BR": "Gorduras, óleos e ceras",
    es: "Grasas, aceites y ceras",
    en: "Animal or vegetable fats and oils",
  },
  IV: {
    "pt-BR": "Produtos das indústrias alimentares; bebidas; tabaco",
    es: "Productos de las industrias alimentarias; bebidas; tabaco",
    en: "Prepared foodstuffs; beverages; tobacco",
  },
  V: {
    "pt-BR": "Produtos minerais",
    es: "Productos minerales",
    en: "Mineral products",
  },
  VI: {
    "pt-BR": "Produtos das indústrias químicas",
    es: "Productos de las industrias químicas",
    en: "Products of the chemical industries",
  },
  VII: {
    "pt-BR": "Plásticos e borracha",
    es: "Plásticos y caucho",
    en: "Plastics and rubber",
  },
  VIII: {
    "pt-BR": "Peles, couros e peles com pelo",
    es: "Pieles, cueros y peletería",
    en: "Hides, skins and furskins",
  },
  IX: {
    "pt-BR": "Madeira e obras de madeira",
    es: "Madera y manufacturas de madera",
    en: "Wood and articles of wood",
  },
  X: {
    "pt-BR": "Pasta de madeira, papel e obras de papel",
    es: "Pasta de madera, papel y sus manufacturas",
    en: "Pulp of wood; paper",
  },
  XI: {
    "pt-BR": "Matérias têxteis e suas obras",
    es: "Materias textiles y sus manufacturas",
    en: "Textiles and textile articles",
  },
  XII: {
    "pt-BR": "Calçado, chapéus e guarda-chuvas",
    es: "Calzado, sombreros y paraguas",
    en: "Footwear, headgear and umbrellas",
  },
  XIII: {
    "pt-BR": "Obras de pedra, cerâmica e vidro",
    es: "Manufacturas de piedra, cerámica y vidrio",
    en: "Articles of stone, ceramic and glass",
  },
  XIV: {
    "pt-BR": "Pérolas, pedras e metais preciosos",
    es: "Perlas, piedras y metales preciosos",
    en: "Pearls, precious stones and metals",
  },
  XV: {
    "pt-BR": "Metais comuns e suas obras",
    es: "Metales comunes y sus manufacturas",
    en: "Base metals and articles of base metal",
  },
  XVI: {
    "pt-BR": "Máquinas e material elétrico",
    es: "Máquinas y material eléctrico",
    en: "Machinery and electrical equipment",
  },
  XVII: {
    "pt-BR": "Material de transporte",
    es: "Material de transporte",
    en: "Vehicles, aircraft and vessels",
  },
  XVIII: {
    "pt-BR": "Instrumentos, relógios e instrumentos musicais",
    es: "Instrumentos, relojes e instrumentos musicales",
    en: "Instruments, clocks and musical instruments",
  },
  XIX: {
    "pt-BR": "Armas e munições",
    es: "Armas y municiones",
    en: "Arms and ammunition",
  },
  XX: {
    "pt-BR": "Mercadorias e produtos diversos",
    es: "Mercancías y productos diversos",
    en: "Miscellaneous manufactured articles",
  },
  XXI: {
    "pt-BR": "Objetos de arte",
    es: "Objetos de arte",
    en: "Works of art",
  },
};

export function ncmChapterSection(
  chapter: string,
  locale: string,
): { id: HsSectionCode; title: string } | null {
  if (!isNcmChapterCode(chapter) || !isChapterLocale(locale)) return null;
  const numeric = Number(chapter);
  const section = SECTION_RANGES.find((range) => numeric >= range.from && numeric <= range.to);
  if (!section) return null;
  return { id: section.id, title: SECTION_TITLES[section.id][locale] };
}

export function ncmChapterNeighbors(chapter: string): {
  previous: NcmChapterCode | null;
  next: NcmChapterCode | null;
} {
  if (!isNcmChapterCode(chapter)) return { previous: null, next: null };
  const index = NCM_CHAPTER_CODES.indexOf(chapter);
  return {
    previous: NCM_CHAPTER_CODES[index - 1] ?? null,
    next: NCM_CHAPTER_CODES[index + 1] ?? null,
  };
}

function foldChapterText(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
}

const TOKEN_SPLIT = /[^a-z0-9]+/;
const TOKEN_CHAR = /[a-z0-9]/;

/** Function words that must not rank a chapter on their own ("para" in SH 14). */
const STOPWORDS = new Set([
  "a",
  "al",
  "an",
  "and",
  "as",
  "at",
  "ao",
  "aos",
  "be",
  "by",
  "com",
  "da",
  "das",
  "de",
  "del",
  "demas",
  "do",
  "dos",
  "e",
  "el",
  "em",
  "en",
  "etc",
  "for",
  "from",
  "in",
  "its",
  "la",
  "las",
  "le",
  "les",
  "los",
  "na",
  "nas",
  "no",
  "nos",
  "o",
  "of",
  "on",
  "or",
  "os",
  "ou",
  "other",
  "others",
  "otras",
  "otros",
  "outras",
  "outros",
  "para",
  "pela",
  "pelas",
  "pelo",
  "pelos",
  "por",
  "sem",
  "seus",
  "suas",
  "sus",
  "the",
  "to",
  "um",
  "uma",
  "un",
  "una",
  "with",
  "y",
]);

function tokenizeChapterText(folded: string): string[] {
  return folded.split(TOKEN_SPLIT).filter((token) => token.length > 0 && !STOPWORDS.has(token));
}

function stemToken(token: string): string {
  if (token.endsWith("es") && token.length - 2 >= 3) return token.slice(0, -2);
  if (token.endsWith("s") && token.length - 1 >= 3) return token.slice(0, -1);
  return token;
}

function queryTokenMatchesTitle(queryToken: string, titleToken: string): boolean {
  if (queryToken === titleToken) return true;
  const queryStem = stemToken(queryToken);
  const titleStem = stemToken(titleToken);
  if (queryStem.length >= 3 && queryStem === titleStem) return true;
  // Prefix typing ("parafu" → "parafusos"): only the query may be the shorter token.
  return queryToken.length >= 4 && titleToken.startsWith(queryToken);
}

function containsAtWordBoundary(haystack: string, needle: string): boolean {
  if (needle.length < 4 || needle.length > haystack.length) return false;
  let from = 0;
  while (from <= haystack.length - needle.length) {
    const index = haystack.indexOf(needle, from);
    if (index === -1) return false;
    const beforeOk = index === 0 || !TOKEN_CHAR.test(haystack[index - 1] ?? "");
    const afterIndex = index + needle.length;
    const afterOk = afterIndex === haystack.length || !TOKEN_CHAR.test(haystack[afterIndex] ?? "");
    if (beforeOk && afterOk) return true;
    from = index + 1;
  }
  return false;
}

function chapterTitleMatches(title: string, foldedQuery: string): boolean {
  const foldedTitle = foldChapterText(title);
  const queryTokens = tokenizeChapterText(foldedQuery);
  if (
    foldedQuery.length >= 4 &&
    queryTokens.length > 0 &&
    containsAtWordBoundary(foldedTitle, foldedQuery)
  ) {
    return true;
  }
  if (queryTokens.length === 0) return false;
  const titleTokens = tokenizeChapterText(foldedTitle);
  return queryTokens.some((queryToken) =>
    titleTokens.some((titleToken) => queryTokenMatchesTitle(queryToken, titleToken)),
  );
}

/**
 * Operator lookup over official SH chapter titles. This names a classification
 * bucket. It does not invent an NCM, a treatment, or a rate.
 */
export function matchNcmChapters(
  query: string,
  locale: string,
  limit = 3,
): Array<{ chapter: NcmChapterCode; title: string }> {
  if (!isChapterLocale(locale)) return [];
  const trimmed = query.trim();
  if (!trimmed) return [];

  const folded = foldChapterText(trimmed);
  const digits = trimmed.replace(/\D/g, "");
  const bareChapter = folded.match(/^(?:sh|hs|capitulo)?\s*(\d{2})$/);
  if (bareChapter?.[1] && isNcmChapterCode(bareChapter[1])) {
    return [{ chapter: bareChapter[1], title: CHAPTER_TITLES[bareChapter[1]][locale] }];
  }

  if (digits.length >= 4) return [];
  if (folded.length < 4) return [];

  const matches: Array<{ chapter: NcmChapterCode; title: string }> = [];
  for (const chapter of Object.keys(CHAPTER_TITLES) as NcmChapterCode[]) {
    const titles = CHAPTER_TITLES[chapter];
    const matched = LOCALES.some((itemLocale) => chapterTitleMatches(titles[itemLocale], folded));
    if (!matched) continue;
    matches.push({ chapter, title: titles[locale] });
    if (matches.length >= limit) break;
  }
  return matches;
}
